import { ReparoApi } from '@api';
import { getBomMaterialPicks, getBomProductPicks } from '@api/bom-management';
import {
    cloneProcurementOrder,
    createProcurementOrder,
    getProcurementOrder,
    listProcurementOrders,
    markProcurementOrderInProduction,
    markProcurementOrderIssued,
    sendProcurementOrder,
    updateProcurementOrder,
    updateProcurementOrderMilestones,
    type CreateProcurementOrderInput,
    type ProcurementOrder,
    type ProcurementOrderItem,
    type ProcurementOrderStatus,
} from '@api/procurement-order';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import useToaster from '@hooks/toaster/useToaster';
import {
    Box,
    Button,
    Checkbox,
    Group,
    Modal,
    NumberInput,
    Select,
    Stack,
    TagsInput,
    Text,
    Textarea,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { DateSinglePicker } from '@components/ui/DateRange';
import {
    getOrderEmailRecipients,
    getOrgConfig,
    orderEmailDefaultsFor,
} from '@api/organization';
import { peekNextPoNumber } from '@api/procurement-order';
import AddPrefixModal from '@components/containers/workOrders/AddPrefixModal';
import woClasses from '@components/containers/workOrders/NewProductionOrderModal.module.css';
import { GST_STATE_OPTIONS, placeOfSupplyFromGstin } from '@diagon-alley/common';
import { usePlaceOfSupplyFromBillTo } from '@hooks/usePlaceOfSupplyFromBillTo';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import {
    DocumentPreview,
    getOrganizationIdFromStorage,
    LineItemsTable,
    type PreviewProps,
} from './formComponents';
import {
    type ChargeDraft,
    CURRENCY_OPTIONS,
    emptyCharge,
    emptyLine,
    mergeImportedLines,
    FieldLabel,
    formatDateDisplay,
    formatMoney,
    type LineDraft,
    MONTHS,
    SectionHeader,
    type SkuOption,
    STATUS_OPTIONS,
    StatusBadge,
    textColors,
    todayIso,
    type VendorOption,
    NO_SKU_OPTIONS,
    useReturnToWorkOrder,
    useWorkOrderPrefill,
    type WorkOrderPrefill,
    WorkOrderPrefillBanner,
    workOrderPrefillLine,
} from './shared';
import { formatCount } from '@utils/currency';




// ─── Document preview (left pane) ──────────────────────────────


// ─── Milestones timeline ───────────────────────────────────────

type MilestoneState = 'reached' | 'scheduled' | 'pending';

type MilestoneDescriptor = {
    key: string;
    label: string;
    state: MilestoneState;
    dateIso: string | null;
    editable?: boolean;
    onSetDate?: (iso: string | null) => void;
};

function isFutureIso(iso: string | null): boolean {
    if (!iso) return false;
    return iso.slice(0, 10) > new Date().toISOString().slice(0, 10);
}

function formatMilestoneDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
}

const PoMilestonesTimeline: FC<{
    order: ProcurementOrder;
    onMilestoneUpdated: () => void;
    onMarkIssued: () => void;
    markingIssued: boolean;
    onMarkInProduction: () => void;
    markingInProduction: boolean;
}> = ({
    order,
    onMilestoneUpdated,
    onMarkIssued,
    markingIssued,
    onMarkInProduction,
    markingInProduction,
}) => {
    const { showToastSuccess, showToastError } = useToaster();
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [saving, setSaving] = useState<string | null>(null);

    const todayIso = new Date().toISOString().slice(0, 10);

    const isCancelled = !!order.cancelledAt;

    const stateForDate = (iso: string | null): MilestoneState => {
        if (!iso) return 'pending';
        return iso.slice(0, 10) <= todayIso ? 'reached' : 'scheduled';
    };

    const persist = async (
        key: 'readinessDate' | 'departureDate' | 'arrivalDate' | 'deliveryDate',
        iso: string | null,
    ) => {
        setSaving(key);
        try {
            await updateProcurementOrderMilestones(order.id, { [key]: iso });
            showToastSuccess({
                title: iso ? 'Milestone updated' : 'Milestone cleared',
            });
            setEditingKey(null);
            onMilestoneUpdated();
        } catch (e: any) {
            const message =
                e?.error?.message ||
                e?.message ||
                'Could not update milestone.';
            showToastError({
                title: 'Could not update milestone',
                subtitle: String(message),
            });
        } finally {
            setSaving(null);
        }
    };

    const milestones: MilestoneDescriptor[] = [
        {
            key: 'created',
            label: 'Created',
            state: 'reached',
            dateIso: (order as any).createdAt ?? order.poDate ?? null,
        },
        {
            key: 'issued',
            label: 'Issued',
            state: order.sentAt ? 'reached' : 'pending',
            dateIso: order.sentAt,
        },
        {
            key: 'inProduction',
            label: 'In Production',
            state: order.inProductionAt ? 'reached' : 'pending',
            // Per PRD: no date displayed for this milestone.
            dateIso: null,
        },
        {
            key: 'readinessDate',
            label: 'Ready to Ship',
            state: stateForDate(order.readinessDate),
            dateIso: order.readinessDate,
            editable: true,
            onSetDate: (iso) => persist('readinessDate', iso),
        },
        {
            key: 'departureDate',
            label: 'Shipped',
            state: stateForDate(order.departureDate),
            dateIso: order.departureDate,
            editable: true,
            onSetDate: (iso) => persist('departureDate', iso),
        },
        {
            key: 'arrivalDate',
            label: 'At Destination Port',
            state: stateForDate(order.arrivalDate),
            dateIso: order.arrivalDate,
            editable: true,
            onSetDate: (iso) => persist('arrivalDate', iso),
        },
        {
            key: 'deliveryDate',
            label: 'Delivered',
            state: stateForDate(order.deliveryDate),
            dateIso: order.deliveryDate,
            editable: true,
            onSetDate: (iso) => persist('deliveryDate', iso),
        },
        {
            key: 'received',
            label: 'Received',
            // Received is GRN-driven, not a user-set date.
            state:
                order.status === 'RECEIVED' || order.status === 'PARTIALLY_RECEIVED'
                    ? 'reached'
                    : 'pending',
            dateIso: null,
        },
    ];

    if (isCancelled) {
        milestones.push({
            key: 'cancelled',
            label: 'Cancelled',
            state: 'reached',
            dateIso: order.cancelledAt,
        });
    }

    const showMarkIssued = order.status === 'DRAFT';
    const showMarkInProduction = order.status === 'ISSUED';

    return (
        <div style={{ padding: '12px 4px' }}>
            <SectionHeader>MILESTONES</SectionHeader>
            <div className="space-y-1">
                {milestones.map((m, idx) => {
                    const grayed =
                        isCancelled && m.key !== 'cancelled' && m.state !== 'reached';
                    const future = isFutureIso(m.dateIso);
                    const isEditing = editingKey === m.key;
                    const isSaving = saving === m.key;

                    const iconColor =
                        m.state === 'reached'
                            ? 'rgb(45, 187, 136)'
                            : m.state === 'scheduled'
                              ? 'rgb(148, 163, 184)'
                              : 'rgb(203, 213, 225)';
                    const iconFill =
                        m.state === 'reached'
                            ? 'rgb(45, 187, 136)'
                            : 'transparent';
                    const iconBorder =
                        m.state === 'scheduled' ? 'dashed' : 'solid';

                    return (
                        <div
                            key={m.key}
                            className="flex items-center"
                            style={{
                                gap: 12,
                                padding: '10px 4px',
                                borderBottom:
                                    idx === milestones.length - 1
                                        ? 'none'
                                        : '1px solid rgb(241, 245, 249)',
                                opacity: grayed ? 0.4 : 1,
                            }}
                        >
                            <span
                                aria-hidden
                                style={{
                                    width: 12,
                                    height: 12,
                                    transform: 'rotate(45deg)',
                                    background: iconFill,
                                    border: `1.5px ${iconBorder} ${iconColor}`,
                                    flexShrink: 0,
                                }}
                            />
                            <span
                                style={{
                                    fontSize: 13,
                                    fontWeight: m.state === 'reached' ? 600 : 500,
                                    color:
                                        m.state === 'reached'
                                            ? textColors.primary
                                            : textColors.secondary,
                                    flex: 1,
                                }}
                            >
                                {m.label}
                            </span>

                            {isEditing && m.editable && m.onSetDate ? (
                                <Group gap={4} wrap="nowrap">
                                    <DateSinglePicker
                                        value={m.dateIso ?? ''}
                                        onChange={(val) =>
                                            m.onSetDate && m.onSetDate(val || null)
                                        }
                                        placeholder="Pick a date"
                                        clearable
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setEditingKey(null)}
                                        disabled={isSaving}
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            color: textColors.muted,
                                            fontSize: 12,
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </Group>
                            ) : m.dateIso ? (
                                <Group gap={6} wrap="nowrap">
                                    <span
                                        style={{
                                            fontSize: 12,
                                            color: future
                                                ? textColors.muted
                                                : textColors.primary,
                                            fontStyle: future ? 'italic' : 'normal',
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {future ? 'Scheduled ' : ''}
                                        {formatMilestoneDate(m.dateIso)}
                                    </span>
                                    {m.editable && !grayed ? (
                                        <button
                                            type="button"
                                            onClick={() => setEditingKey(m.key)}
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                cursor: 'pointer',
                                                color: 'rgb(1, 149, 255)',
                                                fontSize: 11,
                                            }}
                                        >
                                            Edit
                                        </button>
                                    ) : null}
                                </Group>
                            ) : m.editable && !grayed ? (
                                <button
                                    type="button"
                                    onClick={() => setEditingKey(m.key)}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        color: 'rgb(1, 149, 255)',
                                        fontSize: 12,
                                    }}
                                >
                                    Set date
                                </button>
                            ) : (
                                <span
                                    style={{
                                        fontSize: 12,
                                        color: textColors.muted,
                                    }}
                                >
                                    {m.state === 'reached' ? '✓' : '—'}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            {showMarkIssued ? (
                <Button
                    mt="md"
                    size="xs"
                    variant="default"
                    loading={markingIssued}
                    onClick={onMarkIssued}
                    fullWidth
                    title="Mark this PO as Issued without sending the vendor email."
                >
                    Mark as issued
                </Button>
            ) : null}
            {showMarkInProduction ? (
                <Button
                    mt="md"
                    size="xs"
                    variant="default"
                    loading={markingInProduction}
                    onClick={onMarkInProduction}
                    fullWidth
                >
                    Mark as in production
                </Button>
            ) : null}
        </div>
    );
};

// ─── View PO Modal (read-only document) ───────────────────────

function toPreviewProps(order: ProcurementOrder): PreviewProps {
    const charges = (order.additionalCharges ?? []).map((c) => ({
        label: c.label,
        amount: Number(c.amount) || 0,
    }));
    return {
        poNumber: order.poNumber,
        poDate: order.poDate,
        expiryDate: order.expiryDate ?? '',
        status: order.status,
        currency: order.currency ?? 'INR',
        vendorName: order.vendorName ?? '',
        vendorEmail: order.vendorEmail ?? '',
        vendorAddress: order.vendorAddress ?? '',
        vendorGstin: order.vendorGstin ?? '',
        billToName: order.billToName ?? '',
        billToAddress: order.billToAddress ?? '',
        billToGstin: order.billToGstin ?? '',
        shipToName: order.shipToName ?? '',
        shipToEmail: order.shipToEmail ?? '',
        shipToAddress: order.shipToAddress ?? '',
        shipToGstin: order.shipToGstin ?? '',
        shipToStateCode: order.shipToStateCode ?? null,
        contactName: order.contactName ?? '',
        contactEmail: order.contactEmail ?? '',
        lines: (order.items ?? []).map((it) => ({
            sku: '',
            description: it.description,
            quantity: it.quantity,
            unitPrice: it.unitPrice,
            gstRate: it.gstRate ?? ('' as const),
            materialId: it.materialId,
            productId: it.productId,
        })),
        taxRate: order.taxRate,
        subtotal: order.subtotal,
        taxAmount: order.taxAmount,
        charges,
        total: order.total,
        notes: order.notes ?? '',
        terms: order.termsAndConditions ?? '',
    };
}

const ViewPoModal: FC<{
    opened: boolean;
    order: ProcurementOrder | null;
    loading: boolean;
    onClose: () => void;
    onSent: () => void;
    onCloned: (newPoId: string) => void;
    onEdit: (order: ProcurementOrder) => void;
}> = ({ opened, order, loading, onClose, onSent, onCloned, onEdit }) => {
    const { showToastSuccess, showToastError } = useToaster();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [cloning, setCloning] = useState(false);
    const [cc, setCc] = useState<string[]>([]);
    const [to, setTo] = useState<string[]>([]);
    const [updateOrderAddress, setUpdateOrderAddress] = useState(false);

    /*
     * The brand's saved recipient book. One small object per brand, fetched for the screen
     * rather than carried on every order row.
     */
    const emailConfig = useQuery({
        queryKey: ['order-email-recipients', 'purchase-order'],
        queryFn: () => getOrderEmailRecipients('purchase-order'),
    });

    const canSend =
        !!order && !!order.vendorEmail && order.status !== 'CANCELLED';

    const openSendConfirm = () => {
        /*
         * The saved book first, falling back to the order's recorded contact. The two answer
         * different questions — who orders to this supplier go to, and who the order is with —
         * and this dialog is asking the first.
         */
        const defaults = orderEmailDefaultsFor(emailConfig.data, order?.vendorId ?? null);
        setTo(
            defaults.to.length > 0
                ? defaults.to
                : order?.vendorEmail
                  ? [order.vendorEmail]
                  : [],
        );
        setCc(defaults.cc);
        setUpdateOrderAddress(false);
        setConfirmOpen(true);
    };

    const handleSend = async () => {
        if (!order) return;
        setSending(true);
        try {
            await sendProcurementOrder(order.id, { cc, to, updateOrderAddress });
            showToastSuccess({
                title: 'Purchase order sent',
                subtitle: `Emailed to ${to.join(', ')}`,
            });
            setConfirmOpen(false);
            onSent();
        } catch (e: any) {
            const message =
                e?.error?.message ||
                e?.message ||
                'Failed to send the purchase order.';
            showToastError({
                title: 'Could not send purchase order',
                subtitle: String(message),
            });
        } finally {
            setSending(false);
        }
    };

    const handleClone = async () => {
        if (!order) return;
        setCloning(true);
        try {
            const cloned = await cloneProcurementOrder(order.id);
            showToastSuccess({
                title: 'Purchase order cloned',
                subtitle: `Created ${cloned.poNumber} as a new draft.`,
            });
            onCloned(cloned.id);
        } catch (e: any) {
            const message =
                e?.error?.message ||
                e?.message ||
                'Failed to clone the purchase order.';
            showToastError({
                title: 'Could not clone purchase order',
                subtitle: String(message),
            });
        } finally {
            setCloning(false);
        }
    };

    const [markingIssued, setMarkingIssued] = useState(false);
    const handleMarkIssued = async () => {
        if (!order) return;
        setMarkingIssued(true);
        try {
            await markProcurementOrderIssued(order.id);
            showToastSuccess({
                title: 'Marked as issued',
                subtitle: 'No email was sent.',
            });
            onSent();
        } catch (e: any) {
            const message =
                e?.error?.message ||
                e?.message ||
                'Could not mark as issued.';
            showToastError({
                title: 'Could not update status',
                subtitle: String(message),
            });
        } finally {
            setMarkingIssued(false);
        }
    };

    const [markingInProd, setMarkingInProd] = useState(false);
    const handleMarkInProduction = async () => {
        if (!order) return;
        setMarkingInProd(true);
        try {
            await markProcurementOrderInProduction(order.id);
            showToastSuccess({ title: 'Marked as in production' });
            onSent(); // reuse the existing refresh callback
        } catch (e: any) {
            const message =
                e?.error?.message ||
                e?.message ||
                'Could not mark as in production.';
            showToastError({
                title: 'Could not update status',
                subtitle: String(message),
            });
        } finally {
            setMarkingInProd(false);
        }
    };

    return (
        <>
            <Modal
                opened={opened}
                onClose={onClose}
                padding={0}
                withCloseButton={false}
                size={1240}
                centered
            >
                <div
                    className="flex items-center justify-between"
                    style={{
                        height: 52,
                        padding: '0 20px',
                        borderBottom: '1px solid rgb(226, 232, 240)',
                    }}
                >
                    <Text size="15px" fw={600} c={textColors.primary}>
                        {order
                            ? `Purchase Order ${order.poNumber}`
                            : 'Purchase Order'}
                    </Text>
                    <Group gap={8}>
                        {order?.status === 'DRAFT' ? (
                            <Button
                                size="xs"
                                variant="default"
                                onClick={() => order && onEdit(order)}
                                title="Edit this draft purchase order."
                            >
                                Edit
                            </Button>
                        ) : null}
                        <Button
                            size="xs"
                            variant="default"
                            disabled={!order}
                            loading={cloning}
                            title="Create a new draft PO with the same details. Use this to amend an issued PO."
                            onClick={handleClone}
                        >
                            Clone as draft
                        </Button>
                        <Button
                            size="xs"
                            disabled={!canSend}
                            title={
                                order && !order.vendorEmail
                                    ? 'Add a vendor email to this PO before sending'
                                    : undefined
                            }
                            onClick={openSendConfirm}
                        >
                            {order?.sentAt ? 'Resend to vendor' : 'Send to vendor'}
                        </Button>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                fontSize: 18,
                                cursor: 'pointer',
                                color: textColors.secondary,
                            }}
                        >
                            ✕
                        </button>
                    </Group>
                </div>
                <div
                    style={{
                        background: 'rgb(241, 245, 249)',
                        padding: 24,
                        display: 'flex',
                        gap: 20,
                        maxHeight: '78vh',
                        overflowY: 'auto',
                    }}
                >
                    {loading || !order ? (
                        <Text size="13px" c={textColors.muted} py={48} ta="center" w="100%">
                            Loading purchase order...
                        </Text>
                    ) : (
                        <>
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <DocumentPreview {...toPreviewProps(order)} />
                            </div>
                            <div
                                style={{
                                    width: 320,
                                    flexShrink: 0,
                                    background: '#fff',
                                    borderRadius: 6,
                                    padding: 16,
                                    border: '1px solid rgb(226, 232, 240)',
                                    alignSelf: 'flex-start',
                                }}
                            >
                                <PoMilestonesTimeline
                                    order={order}
                                    onMilestoneUpdated={onSent}
                                    onMarkIssued={handleMarkIssued}
                                    markingIssued={markingIssued}
                                    onMarkInProduction={handleMarkInProduction}
                                    markingInProduction={markingInProd}
                                />
                            </div>
                        </>
                    )}
                </div>
            </Modal>

            <Modal
                opened={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title="Send to vendor"
                size="md"
                centered
            >
                {order && (
                    <Stack gap="md">
                        <Text size="sm" c={textColors.secondary}>
                            Purchase order <strong>{order.poNumber}</strong> is
                            about to be emailed.
                            {order.status === 'DRAFT' &&
                                ' It will be marked as Issued.'}
                        </Text>
                        <TagsInput
                            label="To"
                            description="Type an address and press Enter. Pre-filled from Settings -> Email Settings."
                            placeholder="name@supplier.com"
                            value={to}
                            onChange={setTo}
                        />
                        {/*
                          * Offered, not assumed. Sending is a communication act rather than an
                          * amendment - but the person here is usually the one who knows the
                          * right address, and making them go to another screen to record it
                          * means they will not, so the stale contact survives on the order.
                          */}
                        {order.vendorEmail && to.length > 0 && to[0] !== order.vendorEmail && (
                            <Checkbox
                                size="xs"
                                checked={updateOrderAddress}
                                onChange={(e) => setUpdateOrderAddress(e.currentTarget.checked)}
                                label={`Also correct the order's recorded contact (currently ${order.vendorEmail})`}
                                description="Updates the address printed on this order's PDF. Off by default."
                            />
                        )}
                        <TagsInput
                            label="CC"
                            description="Type an address and press Enter. Pre-filled from Settings -> Email Settings."
                            placeholder="ops@yourcompany.com"
                            value={cc}
                            onChange={setCc}
                        />
                        <Text size="xs" c={textColors.muted}>
                            A copy is sent to you, and vendor replies come back to
                            your inbox.
                        </Text>
                        <Group justify="flex-end" gap="sm">
                            <Button
                                variant="default"
                                onClick={() => setConfirmOpen(false)}
                                disabled={sending}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSend}
                                loading={sending}
                                disabled={to.length === 0}
                            >
                                Send
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Modal>
        </>
    );
};

// ─── Line items table (right pane) ─────────────────────────────


// ─── Create PO Modal ───────────────────────────────────────────

// Map a saved line item back into the form's LineDraft shape, recovering the
// SKU code from the catalog by id when possible.
function hydrateLineFromExisting(
    item: ProcurementOrderItem,
    catalog: SkuOption[],
): LineDraft {
    const matched = catalog.find(
        (opt) =>
            (item.materialId &&
                opt.kind === 'material' &&
                opt.id === item.materialId) ||
            (item.productId &&
                opt.kind === 'product' &&
                opt.id === item.productId),
    );
    return {
        sku: matched?.sku ?? '',
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        gstRate: item.gstRate ?? ('' as const),
        materialId: item.materialId,
        productId: item.productId,
    };
}

const PoFormModal: FC<{
    opened: boolean;
    existingOrder?: ProcurementOrder | null;
    workOrderPrefill?: WorkOrderPrefill | null;
    onClose: () => void;
    vendors: VendorOption[];
    skuOptions: SkuOption[];
    onSaved: () => void;
}> = ({ opened, existingOrder, workOrderPrefill, onClose, vendors, skuOptions, onSaved }) => {
    const isEditMode = !!existingOrder;
    const { showToastError, showToastSuccess } = useToaster();
    const [status, setStatus] = useState<ProcurementOrderStatus>('DRAFT');
    const [currency, setCurrency] = useState('INR');
    const [poNumber, setPoNumber] = useState('');
    /** Which stem the number was built from. Only ever the picker's own state. */
    const [numberPrefix, setNumberPrefix] = useState<string | null>(null);
    const [isAddingPrefix, setIsAddingPrefix] = useState(false);
    const [poDate, setPoDate] = useState(todayIso());
    const [expiryDate, setExpiryDate] = useState('');
    const [vendorId, setVendorId] = useState<string | null>(null);
    const [vendorName, setVendorName] = useState('');
    const [vendorEmail, setVendorEmail] = useState('');
    const [vendorAddress, setVendorAddress] = useState('');
    const [vendorGstin, setVendorGstin] = useState('');
    const [billToName, setBillToName] = useState('');
    const [billToAddress, setBillToAddress] = useState('');
    const [billToGstin, setBillToGstin] = useState('');
    const [shipToName, setShipToName] = useState('');
    const [shipToEmail, setShipToEmail] = useState('');
    const [shipToAddress, setShipToAddress] = useState('');
    const [shipToGstin, setShipToGstin] = useState('');
    const [shipToStateCode, setShipToStateCode] = useState<string | null>(null);
    /** True once somebody picks a state by hand, after which the GSTIN stops driving it. */
    const [placeOfSupplyPicked, setPlaceOfSupplyPicked] = useState(false);
    /** Held by hand or by the Bill To GSTIN; the Ship To side seeds the state only while false. */
    const placeOfSupplyLocked = usePlaceOfSupplyFromBillTo(billToGstin, placeOfSupplyPicked, setShipToStateCode);
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [taxRate, setTaxRate] = useState<number | ''>(18);
    const [notes, setNotes] = useState('');
    const [terms, setTerms] = useState('');
    const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
    const [charges, setCharges] = useState<ChargeDraft[]>([]);
    const [saving, setSaving] = useState(false);
    const [sendingAfterCreate, setSendingAfterCreate] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
    const [sendCc, setSendCc] = useState<string[]>([]);

    useEffect(() => {
        if (!opened) return;
        setSaving(false);
        setSendingAfterCreate(false);
        setError(null);
        setSendConfirmOpen(false);
        setSendCc([]);

        if (existingOrder) {
            setStatus(existingOrder.status);
            setCurrency(existingOrder.currency || 'INR');
            setPoNumber(existingOrder.poNumber);
            setPoDate(existingOrder.poDate || todayIso());
            setExpiryDate(existingOrder.expiryDate ?? '');
            setVendorId(existingOrder.vendorId);
            setVendorName(existingOrder.vendorName ?? '');
            setVendorEmail(existingOrder.vendorEmail ?? '');
            setVendorAddress(existingOrder.vendorAddress ?? '');
            setVendorGstin(existingOrder.vendorGstin ?? '');
            setBillToName(existingOrder.billToName ?? '');
            setBillToAddress(existingOrder.billToAddress ?? '');
            setBillToGstin(existingOrder.billToGstin ?? '');
            setShipToName(existingOrder.shipToName ?? '');
            setShipToEmail(existingOrder.shipToEmail ?? '');
            setShipToAddress(existingOrder.shipToAddress ?? '');
            setShipToGstin(existingOrder.shipToGstin ?? '');
            setShipToStateCode(existingOrder.shipToStateCode ?? null);
            // An order already on file states its own place of supply; the GSTIN must not move it.
            setPlaceOfSupplyPicked(true);
            setContactName(existingOrder.contactName ?? '');
            setContactEmail(existingOrder.contactEmail ?? '');
            setTaxRate(Number(existingOrder.taxRate) || 0);
            setNotes(existingOrder.notes ?? '');
            setTerms(existingOrder.termsAndConditions ?? '');
            const hydratedLines = (existingOrder.items ?? []).map((it) =>
                hydrateLineFromExisting(it, skuOptions),
            );
            setLines(hydratedLines.length ? hydratedLines : [emptyLine()]);
            setCharges(
                (existingOrder.additionalCharges ?? []).map((c) => ({
                    label: c.label,
                    amount: Number(c.amount) || 0,
                })),
            );
        } else {
            setStatus('DRAFT');
            setCurrency('INR');
            setPoNumber('');
            setPoDate(todayIso());
            setExpiryDate('');
            setVendorId(null);
            setVendorName('');
            setVendorEmail('');
            setVendorAddress('');
            setVendorGstin('');
            setBillToName('');
            setBillToAddress('');
            setBillToGstin('');
            setShipToName('');
            setShipToEmail('');
            setShipToAddress('');
            setShipToGstin('');
            setShipToStateCode(null);
            setPlaceOfSupplyPicked(false);
            setContactName('');
            setContactEmail('');
            setTaxRate(18);
            setNotes('');
            setTerms('');
            setCharges([]);

            setLines([workOrderPrefill ? workOrderPrefillLine(workOrderPrefill, skuOptions) : emptyLine()]);
        }
    }, [opened, existingOrder, skuOptions, workOrderPrefill]);

    /**
     * The brand's standard terms, seeded into a NEW order only.
     *
     * AN EXISTING ORDER KEEPS ITS OWN, whatever they are — including none. Most orders on file
     * predate this setting and carry no terms at all, and filling those in on open would show a
     * document terms it was never sent with.
     *
     * Seeded once per opening, and only into an untouched box, so clearing them for a one-off
     * order sticks rather than being undone by the next render.
     */
    const { data: orgForTerms } = useQuery(
        ['org-config', getOrganizationIdFromStorage()],
        () => getOrgConfig(getOrganizationIdFromStorage() as string),
        { enabled: !!getOrganizationIdFromStorage() },
    );
    /**
     * The brand's purchase-order stems, off the org config this modal already fetches for the
     * terms. Absent for a brand that has never opened Settings > Order Numbering, and the
     * picker hides itself - the field stays the single "Auto-generated if blank" box.
     */
    const queryClient = useQueryClient();

    const poPrefixes = useMemo(
        () =>
            (orgForTerms?.documentNumbering?.PURCHASE_ORDER?.prefixes ?? []).filter((entry) =>
                entry.value?.trim(),
            ),
        [orgForTerms],
    );

    /**
     * Picking a stem fills the number in - the server works out what comes next, because the
     * client cannot see the orders somebody else raised this morning. A failure leaves the
     * prefix alone in the box: still a usable start, completed at save.
     */
    const applyPoPrefix = (prefix: string | null) => {
        setNumberPrefix(prefix);
        if (!prefix) return;

        const padWidth = orgForTerms?.documentNumbering?.PURCHASE_ORDER?.padWidth;
        void peekNextPoNumber(prefix, padWidth)
            .then((next) => setPoNumber(next || prefix))
            .catch(() => setPoNumber(prefix));
    };

    /**
     * A GSTIN carries its state in the first two digits, so typing one settles the place of
     * supply, but only while nothing stronger holds it: a state chosen by hand, or the Bill To
     * GSTIN, which names the place of supply whenever there is one (usePlaceOfSupplyFromBillTo).
     */
    const handleShipToGstin = (value: string) => {
        setShipToGstin(value);
        if (placeOfSupplyLocked) return;

        const derived = placeOfSupplyFromGstin(value);
        if (derived) setShipToStateCode(derived);
    };

    const seededTerms = useRef(false);
    useEffect(() => {
        if (!opened) {
            seededTerms.current = false;
            return;
        }
        if (existingOrder || seededTerms.current || !orgForTerms) return;
        seededTerms.current = true;
        setTerms((current) => current || (orgForTerms.documentTerms ?? ''));
    }, [opened, existingOrder, orgForTerms]);

    /**
     * Bill To is this brand billing itself, so it is the same three values on every order.
     *
     * Seeded on the same terms as the standard terms above, `existingOrder` guard included: an
     * order already on file carries the Bill To it was raised with, and a document must keep
     * saying what it said. For a new one this saves retyping the brand's own registered name and
     * GSTIN, which is worth doing because the document prints the two side by side — a mistyped
     * character there is a GST document whose name does not match the number beside it.
     *
     * `legalName` is the registered entity and `name` the trading one; the document wants the
     * first and falls back to the second, which is all a brand yet to fill in the letterhead has.
     */
    const seededBillTo = useRef(false);
    useEffect(() => {
        if (!opened) {
            seededBillTo.current = false;
            return;
        }
        if (existingOrder || seededBillTo.current || !orgForTerms) return;
        seededBillTo.current = true;
        setBillToName((current) => current || orgForTerms.legalName || orgForTerms.name || '');
        setBillToAddress((current) => current || (orgForTerms.address ?? ''));
        setBillToGstin((current) => current || (orgForTerms.gstin ?? ''));
    }, [opened, existingOrder, orgForTerms]);

    const subtotal = useMemo(
        () =>
            lines.reduce(
                (sum, l) =>
                    sum + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0),
                0,
            ),
        [lines],
    );
    const taxAmount = (subtotal * (Number(taxRate) || 0)) / 100;
    const chargesTotal = useMemo(
        () => charges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0),
        [charges],
    );
    const total = subtotal + taxAmount + chargesTotal;

    const handleVendorChange = async (id: string | null) => {
        setVendorId(id);
        if (!id) return;
        try {
            const v = await ReparoApi<{
                name?: string;
                email?: string;
                street?: string;
                city?: string;
                country?: string;
                zip?: string;
                taxCode?: string;
            }>({ method: 'GET', url: `/vendors/${id}` });
            if (v) {
                setVendorName(v.name ?? '');
                setVendorEmail(v.email ?? '');
                setVendorAddress(
                    [v.street, v.city, v.country, v.zip]
                        .filter(Boolean)
                        .join(', '),
                );
                setVendorGstin(v.taxCode ?? '');
            }
        } catch {
            // vendor detail unavailable - fields stay editable
        }
    };

    /** SKUs already on the order: greyed in the dropdown, so a second line cannot be opened. */
    const pickedSkus = useMemo(() => new Set(lines.map((l) => l.sku).filter(Boolean)), [lines]);
    const skuData = useMemo(
        () =>
            skuOptions.map((o) => ({
                value: o.sku,
                label: `${o.sku} — ${o.name}`,
                disabled: pickedSkus.has(o.sku),
            })),
        [skuOptions, pickedSkus],
    );

    const updateLine = (idx: number, patch: Partial<LineDraft>) => {
        setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    };

    const handleSkuPicked = (idx: number, sku: string) => {
        // One line per SKU: the dropdown greys a picked SKU, this catches every other path.
        const already = lines.findIndex((l, i) => i !== idx && l.sku === sku);
        if (already !== -1) {
            showToastError({
                title: 'Already on this order',
                subtitle: `Edit the quantity on line ${already + 1} instead.`,
            });
            return;
        }
        const found = skuOptions.find((o) => o.sku === sku);
        if (!found) {
            updateLine(idx, { sku });
            return;
        }
        updateLine(idx, {
            sku,
            description: found.name,
            materialId: found.kind === 'material' ? found.id : null,
            productId: found.kind === 'product' ? found.id : null,
        });
    };

    const updateCharge = (idx: number, patch: Partial<ChargeDraft>) => {
        setCharges((prev) =>
            prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
        );
    };

    const handleSave = async (options?: { andSend?: boolean }) => {
        const andSend = options?.andSend === true;
        const items = lines
            .filter((l) => l.description.trim() || l.sku.trim())
            .map((l) => ({
                description: l.description.trim() || l.sku.trim(),
                materialId: l.materialId,
                productId: l.productId,
                quantity: Number(l.quantity) || 0,
                unitPrice: Number(l.unitPrice) || 0,
                gstRate: l.gstRate === '' ? null : Number(l.gstRate),
            }));

        if (items.length === 0) {
            setError('Add at least one line item.');
            return;
        }

        const additionalCharges = charges
            .filter((c) => c.label.trim())
            .map((c) => ({
                label: c.label.trim(),
                amount: Number(c.amount) || 0,
            }));

        const sharedFields = {
            currency,
            poDate: poDate || undefined,
            expiryDate: expiryDate || undefined,
            vendorId: vendorId || undefined,
            vendorName: vendorName.trim() || undefined,
            vendorEmail: vendorEmail.trim() || undefined,
            vendorAddress: vendorAddress.trim() || undefined,
            vendorGstin: vendorGstin.trim() || undefined,
            billToName: billToName.trim() || undefined,
            billToAddress: billToAddress.trim() || undefined,
            billToGstin: billToGstin.trim() || undefined,
            shipToName: shipToName.trim() || undefined,
            shipToAddress: shipToAddress.trim() || undefined,
            shipToGstin: shipToGstin.trim() || undefined,
            shipToStateCode: shipToStateCode || undefined,
            shipToEmail: shipToEmail.trim() || undefined,
            contactName: contactName.trim() || undefined,
            contactEmail: contactEmail.trim() || undefined,
            taxRate: Number(taxRate) || 0,
            notes: notes.trim() || undefined,
            termsAndConditions: terms.trim() || undefined,
            items,
            additionalCharges,
        };

        if (andSend) setSendingAfterCreate(true);
        else setSaving(true);
        setError(null);
        try {
            if (isEditMode && existingOrder) {
                await updateProcurementOrder(existingOrder.id, sharedFields);
                onSaved();
                onClose();
            } else {
                const createPayload: CreateProcurementOrderInput = {
                    ...sharedFields,
                    status,
                    poNumber: poNumber.trim() || undefined,
                    ...(workOrderPrefill
                        ? { workOrderId: workOrderPrefill.workOrderId }
                        : {}),
                };
                const created = await createProcurementOrder(createPayload);
                if (andSend) {
                    try {
                        await sendProcurementOrder(created.id, {
                            cc: sendCc,
                        });
                    } catch (sendErr: any) {
                        const sendMessage =
                            sendErr?.error?.message ||
                            sendErr?.message ||
                            'Purchase order created, but the email could not be sent.';
                        showToastError({
                            title: 'Could not send purchase order',
                            subtitle: String(sendMessage),
                        });
                    }
                }
                onSaved();
                onClose();
            }
        } catch (e: any) {
            const message =
                e?.error?.message ||
                e?.message ||
                (isEditMode
                    ? 'Could not save changes to the purchase order.'
                    : 'Could not create the purchase order.');
            setError(String(message));
            if (isEditMode) {
                // Show a toast on edit failures so the modal-internal error is
                // never missed if the user scrolls past it.
                showToastError({
                    title: 'Could not save purchase order',
                    subtitle: String(message),
                });
            }
        } finally {
            setSaving(false);
            setSendingAfterCreate(false);
        }
    };

    const vendorData = vendors.map((v) => ({
        value: v.vendorId,
        label: v.name || v.vendorId,
    }));

    const previewCharges = charges.map((c) => ({
        label: c.label,
        amount: Number(c.amount) || 0,
    }));

    const paneStyle: React.CSSProperties = {
        flex: 1,
        minWidth: 0,
        overflowY: 'auto',
    };

    return (
        <>
        <Modal
            opened={opened}
            onClose={onClose}
            fullScreen
            padding={0}
            withCloseButton={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <div
                    className="flex items-center justify-between"
                    style={{
                        height: 56,
                        padding: '0 24px',
                        borderBottom: '1px solid rgb(226, 232, 240)',
                        flexShrink: 0,
                    }}
                >
                    <Text size="15px" fw={600} c={textColors.primary}>
                        {isEditMode
                            ? `Edit Purchase Order ${existingOrder?.poNumber ?? ''}`
                            : 'Create Purchase Order'}
                    </Text>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            fontSize: 18,
                            cursor: 'pointer',
                            color: textColors.secondary,
                        }}
                    >
                        ✕
                    </button>
                </div>

                {workOrderPrefill ? <WorkOrderPrefillBanner prefill={workOrderPrefill} /> : null}

                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    {/* Left: live document preview */}
                    <div
                        style={{
                            ...paneStyle,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            background: 'rgb(241, 245, 249)',
                            padding: 28,
                        }}
                    >
                        <DocumentPreview
                            poNumber={poNumber}
                            poDate={poDate}
                            expiryDate={expiryDate}
                            status={status}
                            currency={currency}
                            vendorName={vendorName}
                            vendorEmail={vendorEmail}
                            vendorAddress={vendorAddress}
                            vendorGstin={vendorGstin}
                            billToName={billToName}
                            billToAddress={billToAddress}
                            billToGstin={billToGstin}
                            shipToName={shipToName}
                            shipToEmail={shipToEmail}
                            shipToAddress={shipToAddress}
                            shipToGstin={shipToGstin}
                            shipToStateCode={shipToStateCode}
                            contactName={contactName}
                            contactEmail={contactEmail}
                            lines={lines}
                            taxRate={Number(taxRate) || 0}
                            subtotal={subtotal}
                            taxAmount={taxAmount}
                            charges={previewCharges}
                            total={total}
                            notes={notes}
                            terms={terms}
                        />
                    </div>

                    {/* Right: edit panel */}
                    <div
                        style={{
                            ...paneStyle,
                            borderLeft: '1px solid rgb(226, 232, 240)',
                            padding: 28,
                        }}
                    >
                        <SectionHeader>DETAILS</SectionHeader>
                        <div className="space-y-3">
                            <Group grow>
                                {isEditMode ? (
                                    <div>
                                        <FieldLabel>Status</FieldLabel>
                                        <div style={{ paddingTop: 4 }}>
                                            <StatusBadge status={status} />
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <FieldLabel>Status</FieldLabel>
                                        <Select
                                            data={STATUS_OPTIONS}
                                            value={status}
                                            onChange={(val) =>
                                                setStatus(
                                                    (val as ProcurementOrderStatus) ??
                                                        'DRAFT',
                                                )
                                            }
                                            allowDeselect={false}
                                        />
                                    </div>
                                )}
                                <div>
                                    <FieldLabel>Currency</FieldLabel>
                                    <Select
                                        data={CURRENCY_OPTIONS}
                                        value={currency}
                                        onChange={(val) =>
                                            setCurrency(val ?? 'INR')
                                        }
                                        allowDeselect={false}
                                        searchable
                                    />
                                </div>
                            </Group>
                            <div>
                                {/*
                                  * Mirrors the production-order form: the stem and the number,
                                  * when the brand has defined stems - CREATE ONLY. In edit the
                                  * number is the order's identity and stays a read-only box.
                                  */}
                                <Group justify="space-between" align="baseline" gap={8}>
                                    <FieldLabel>PO Number</FieldLabel>
                                    {!isEditMode && (
                                        <button
                                            type="button"
                                            className={woClasses.createLink}
                                            onClick={() => setIsAddingPrefix(true)}
                                        >
                                            + Prefix
                                        </button>
                                    )}
                                </Group>
                                {!isEditMode && poPrefixes.length > 0 ? (
                                    <Group gap={6} wrap="nowrap" align="flex-start">
                                        <Select
                                            placeholder="Prefix"
                                            data={[
                                                ...poPrefixes.map((entry) => entry.value.trim()),
                                                { value: '__add_prefix__', label: '+ Add prefix' },
                                            ]}
                                            value={numberPrefix}
                                            onChange={(picked) => {
                                                if (picked === '__add_prefix__') {
                                                    setIsAddingPrefix(true);
                                                    return;
                                                }
                                                applyPoPrefix(picked);
                                            }}
                                            clearable
                                            comboboxProps={{ width: 220, position: 'bottom-start' }}
                                            w={150}
                                        />
                                        <TextInput
                                            placeholder="Auto-generated if blank"
                                            value={poNumber}
                                            onChange={(e) => setPoNumber(e.currentTarget.value)}
                                            flex={1}
                                        />
                                    </Group>
                                ) : (
                                    <TextInput
                                        placeholder="Auto-generated if blank"
                                        value={poNumber}
                                        onChange={(e) => setPoNumber(e.currentTarget.value)}
                                        readOnly={isEditMode}
                                        disabled={isEditMode}
                                    />
                                )}
                            </div>
                            <Group grow>
                                <div>
                                    <FieldLabel>Issue Date</FieldLabel>
                                    <DateSinglePicker
                                        value={poDate}
                                        onChange={setPoDate}
                                        placeholder="Pick a date"
                                    />
                                </div>
                                <div>
                                    <FieldLabel>Expiry Date</FieldLabel>
                                    <DateSinglePicker
                                        value={expiryDate}
                                        onChange={setExpiryDate}
                                        placeholder="Pick a date"
                                        clearable
                                    />
                                </div>
                            </Group>
                        </div>

                        <SectionHeader>VENDOR</SectionHeader>
                        <div className="space-y-2">
                            <Select
                                placeholder="Search and select a vendor"
                                data={vendorData}
                                value={vendorId}
                                onChange={handleVendorChange}
                                searchable
                                clearable
                                nothingFoundMessage="No vendors found"
                            />
                            <TextInput
                                placeholder="Vendor name"
                                value={vendorName}
                                onChange={(e) =>
                                    setVendorName(e.currentTarget.value)
                                }
                            />
                            <TextInput
                                placeholder="Email"
                                value={vendorEmail}
                                onChange={(e) =>
                                    setVendorEmail(e.currentTarget.value)
                                }
                            />
                            <TextInput
                                placeholder="Address"
                                value={vendorAddress}
                                onChange={(e) =>
                                    setVendorAddress(e.currentTarget.value)
                                }
                            />
                            <TextInput
                                placeholder="GST number"
                                value={vendorGstin}
                                onChange={(e) =>
                                    setVendorGstin(e.currentTarget.value)
                                }
                            />
                        </div>

                        <SectionHeader>BILL TO</SectionHeader>
                        <div className="space-y-2">
                            <TextInput
                                placeholder="Business name"
                                value={billToName}
                                onChange={(e) =>
                                    setBillToName(e.currentTarget.value)
                                }
                            />
                            {/*
                              * Textarea, not TextInput: a registered address is multi-line and
                              * the letterhead stores it with newlines. A single-line input
                              * silently STRIPS them, turning "...Gurgaon\nGurugram..." into
                              * "...GurgaonGurugram...", and that mangled value is what would be
                              * saved onto the order. Matches the production order modal and the
                              * letterhead screen, which both render this field as a textarea.
                              */}
                            <Textarea
                                placeholder="Address"
                                value={billToAddress}
                                onChange={(e) =>
                                    setBillToAddress(e.currentTarget.value)
                                }
                                autosize
                                minRows={2}
                            />
                            <TextInput
                                placeholder="GST number"
                                value={billToGstin}
                                onChange={(e) =>
                                    setBillToGstin(e.currentTarget.value)
                                }
                            />
                        </div>

                        <SectionHeader>SHIP TO</SectionHeader>
                        <div className="space-y-2">
                            <TextInput
                                placeholder="Name"
                                value={shipToName}
                                onChange={(e) =>
                                    setShipToName(e.currentTarget.value)
                                }
                            />
                            <TextInput
                                placeholder="Email"
                                value={shipToEmail}
                                onChange={(e) =>
                                    setShipToEmail(e.currentTarget.value)
                                }
                            />
                            <TextInput
                                placeholder="Address"
                                value={shipToAddress}
                                onChange={(e) =>
                                    setShipToAddress(e.currentTarget.value)
                                }
                            />
                            <TextInput
                                placeholder="GST number"
                                value={shipToGstin}
                                onChange={(e) => handleShipToGstin(e.currentTarget.value)}
                            />
                            <Select
                                placeholder="Place of supply (state) — sets IGST vs CGST+SGST"
                                data={GST_STATE_OPTIONS}
                                value={shipToStateCode}
                                onChange={(value) => {
                                    setPlaceOfSupplyPicked(true);
                                    setShipToStateCode(value);
                                }}
                                searchable
                                clearable
                                nothingFoundMessage="No matching state"
                            />
                        </div>

                        <SectionHeader>CONTACT DETAIL</SectionHeader>
                        <div className="space-y-2">
                            <TextInput
                                placeholder="Name"
                                value={contactName}
                                onChange={(e) =>
                                    setContactName(e.currentTarget.value)
                                }
                            />
                            <TextInput
                                placeholder="Email"
                                value={contactEmail}
                                onChange={(e) =>
                                    setContactEmail(e.currentTarget.value)
                                }
                            />
                        </div>

                        <SectionHeader>LINE ITEMS</SectionHeader>
                        <LineItemsTable
                            lines={lines}
                            skuData={skuData}
                            currency={currency}
                            onUpdateLine={updateLine}
                            onSkuPicked={handleSkuPicked}
                            onRemoveLine={(idx) =>
                                setLines((prev) =>
                                    prev.filter((_, i) => i !== idx),
                                )
                            }
                            onAddLine={() =>
                                setLines((prev) => [...prev, emptyLine()])
                            }
                            onClearAll={() => setLines([emptyLine()])}
                            skuOptions={skuOptions}
                            onImportLines={(imported) => {
                                const { lines: next, mergedRows } = mergeImportedLines(lines, imported);
                                setLines(next);
                                if (mergedRows > 0) {
                                    showToastSuccess({
                                        title: `${mergedRows} row${mergedRows === 1 ? '' : 's'} added to lines already on the order`,
                                    });
                                }
                            }}
                        />

                        <SectionHeader>ADDITIONAL CHARGES</SectionHeader>
                        <div className="space-y-2">
                            {charges.length === 0 ? (
                                <Text size="12px" c={textColors.muted}>
                                    No additional charges.
                                </Text>
                            ) : (
                                charges.map((charge, idx) => (
                                    <Group key={idx} gap={8} wrap="nowrap">
                                        <TextInput
                                            placeholder="Charge (e.g. Shipping)"
                                            value={charge.label}
                                            onChange={(e) =>
                                                updateCharge(idx, {
                                                    label: e.currentTarget
                                                        .value,
                                                })
                                            }
                                            size="xs"
                                            style={{ flex: 2 }}
                                        />
                                        <NumberInput
                                            placeholder="Amount"
                                            value={charge.amount}
                                            onChange={(val) =>
                                                updateCharge(idx, {
                                                    amount:
                                                        typeof val === 'number'
                                                            ? val
                                                            : '',
                                                })
                                            }
                                            min={0}
                                            size="xs"
                                            hideControls
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setCharges((prev) =>
                                                    prev.filter(
                                                        (_, i) => i !== idx,
                                                    ),
                                                )
                                            }
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                color: 'rgb(205, 101, 101)',
                                                cursor: 'pointer',
                                                fontSize: 13,
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </Group>
                                ))
                            )}
                            <Button
                                variant="subtle"
                                size="xs"
                                onClick={() =>
                                    setCharges((prev) => [
                                        ...prev,
                                        emptyCharge(),
                                    ])
                                }
                            >
                                + Add charge
                            </Button>
                        </div>

                        <SectionHeader>TOTALS</SectionHeader>
                        <div
                            style={{
                                border: '1px solid rgb(226, 232, 240)',
                                borderRadius: 6,
                                padding: 12,
                            }}
                        >
                            <div
                                className="flex justify-between"
                                style={{ fontSize: 12, marginBottom: 6 }}
                            >
                                <span style={{ color: textColors.secondary }}>
                                    Subtotal
                                </span>
                                <span style={{ color: textColors.primary }}>
                                    {formatMoney(subtotal, currency)}
                                </span>
                            </div>
                            <div
                                className="flex items-center justify-between"
                                style={{ fontSize: 12, marginBottom: 6 }}
                            >
                                <Group gap={6} align="center">
                                    <span
                                        style={{ color: textColors.secondary }}
                                    >
                                        Tax
                                    </span>
                                    <NumberInput
                                        value={taxRate}
                                        onChange={(val) =>
                                            setTaxRate(
                                                typeof val === 'number'
                                                    ? val
                                                    : '',
                                            )
                                        }
                                        min={0}
                                        max={100}
                                        size="xs"
                                        w={64}
                                        suffix="%"
                                        hideControls
                                    />
                                </Group>
                                <span style={{ color: textColors.primary }}>
                                    {formatMoney(taxAmount, currency)}
                                </span>
                            </div>
                            {chargesTotal > 0 ? (
                                <div
                                    className="flex justify-between"
                                    style={{ fontSize: 12, marginBottom: 6 }}
                                >
                                    <span
                                        style={{ color: textColors.secondary }}
                                    >
                                        Additional charges
                                    </span>
                                    <span style={{ color: textColors.primary }}>
                                        {formatMoney(chargesTotal, currency)}
                                    </span>
                                </div>
                            ) : null}
                            <div
                                className="flex justify-between"
                                style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    paddingTop: 6,
                                    borderTop: '1px solid rgb(226, 232, 240)',
                                    color: textColors.primary,
                                }}
                            >
                                <span>Total</span>
                                <span>{formatMoney(total, currency)}</span>
                            </div>
                        </div>

                        <SectionHeader>NOTES &amp; TERMS</SectionHeader>
                        <div className="space-y-2">
                            <Textarea
                                placeholder="Notes"
                                value={notes}
                                onChange={(e) => setNotes(e.currentTarget.value)}
                                autosize
                                minRows={2}
                            />
                            <Textarea
                                placeholder="Terms & conditions"
                                value={terms}
                                onChange={(e) => setTerms(e.currentTarget.value)}
                                autosize
                                minRows={3}
                            />
                        </div>
                    </div>
                </div>

                <div
                    className="flex items-center justify-between"
                    style={{
                        height: 60,
                        padding: '0 24px',
                        borderTop: '1px solid rgb(226, 232, 240)',
                        flexShrink: 0,
                    }}
                >
                    <Text size="13px" c="rgb(205, 101, 101)">
                        {error ?? ''}
                    </Text>
                    <Group>
                        <Button
                            variant="default"
                            onClick={onClose}
                            disabled={saving || sendingAfterCreate}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={isEditMode ? 'filled' : 'default'}
                            onClick={() => handleSave()}
                            loading={saving}
                            disabled={sendingAfterCreate}
                        >
                            {isEditMode ? 'Save changes' : 'Create PO'}
                        </Button>
                        {!isEditMode ? (
                            <Tooltip
                                label="Add a vendor email to send the PO."
                                disabled={vendorEmail.trim().length > 0}
                                withArrow
                            >
                                <Button
                                    onClick={() => {
                                        setSendCc([]);
                                        setSendConfirmOpen(true);
                                    }}
                                    loading={sendingAfterCreate}
                                    disabled={
                                        saving || vendorEmail.trim().length === 0
                                    }
                                >
                                    Send PO to Vendor
                                </Button>
                            </Tooltip>
                        ) : null}
                    </Group>
                </div>
            </div>
        </Modal>

        <Modal
            opened={sendConfirmOpen}
            onClose={() => setSendConfirmOpen(false)}
            title="Send to vendor"
            size="md"
            centered
            zIndex={2000}
        >
            <Stack gap="md">
                <Text size="sm" c={textColors.secondary}>
                    The purchase order will be created and emailed to{' '}
                    <strong>{vendorEmail || '—'}</strong>. It will be marked as
                    Issued.
                </Text>
                <Text size="xs" c={textColors.muted}>
                    A copy is sent to you, and vendor replies come back to your
                    inbox.
                </Text>
                <TagsInput
                    label="CC"
                    description="Type an address and press Enter."
                    placeholder="ops@yourcompany.com"
                    value={sendCc}
                    onChange={setSendCc}
                />
                <Group justify="flex-end" gap="sm">
                    <Button
                        variant="default"
                        onClick={() => setSendConfirmOpen(false)}
                        disabled={sendingAfterCreate}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            setSendConfirmOpen(false);
                            await handleSave({ andSend: true });
                        }}
                        loading={sendingAfterCreate}
                    >
                        Send
                    </Button>
                </Group>
            </Stack>
        </Modal>

        {/* The same modal the production-order form and Settings use - one set of rules
            about what a prefix is, whichever door it is minted through. */}
        <AddPrefixModal
            opened={isAddingPrefix}
            onClose={() => setIsAddingPrefix(false)}
            documentType="PURCHASE_ORDER"
            existing={poPrefixes}
            onSaved={(saved) => {
                void queryClient.invalidateQueries({
                    queryKey: ['org-config', getOrganizationIdFromStorage()],
                });
                applyPoPrefix(saved);
            }}
        />
        </>
    );
};

// ─── Container ─────────────────────────────────────────────────

const ProcurementOrdersContainer: FC = () => {
    const queryClient = useQueryClient();
    const [formOpen, setFormOpen] = useState(false);
    const [editingOrder, setEditingOrder] = useState<ProcurementOrder | null>(
        null,
    );
    const [detailId, setDetailId] = useState<string | null>(null);
    const [workOrderPrefill, setWorkOrderPrefill] = useWorkOrderPrefill(() => {
        setEditingOrder(null);
        setFormOpen(true);
    });
    const returnToWorkOrder = useReturnToWorkOrder();

    const closeForm = () => {
        setFormOpen(false);
        setEditingOrder(null);
        setWorkOrderPrefill(null);
    };
    const openCreate = () => {
        setEditingOrder(null);
        setWorkOrderPrefill(null);
        setFormOpen(true);
    };
    const openEdit = (order: ProcurementOrder) => {
        setDetailId(null);
        setEditingOrder(order);
        setFormOpen(true);
    };

    const { data: orders, isLoading } = useQuery(['procurement-orders'], () =>
        listProcurementOrders(),
    );

    const { data: detailOrder, isFetching: detailLoading } = useQuery(
        ['procurement-order', detailId],
        () => getProcurementOrder(detailId as string),
        { enabled: detailId !== null },
    );

    const { data: vendors } = useQuery(['vendors-for-po'], async () => {
        const rows = await ReparoApi<VendorOption[]>({
            method: 'GET',
            url: '/vendors?limit=200',
        });
        return (rows ?? []).filter((v) => v.vendorId);
    });

    const { data: skuCatalog } = useQuery(['po-sku-catalog'], async () => {
        const [materials, products] = await Promise.all([
            getBomMaterialPicks(),
            getBomProductPicks(),
        ]);
        const opts: SkuOption[] = [];
        for (const m of materials) {
            // A material with no SKU of its own is keyed by its id. Whole catalogues carry
            // none (Troovy, koparo), and dropping them made every raw and packing material
            // unorderable here. The key is only the picker's value and the CSV's match
            // column; saving sends the material id.
            const key = m.sku || m.materialId;
            if (key) {
                opts.push({
                    sku: key,
                    name: m.name,
                    kind: 'material',
                    id: m.materialId,
                });
            }
        }
        for (const p of products) {
            if (p.sku) {
                opts.push({
                    sku: p.sku,
                    name: p.name,
                    kind: 'product',
                    id: p.productId,
                    unitsPerCase: p.unitsPerCase ?? null,
                });
            }
        }
        return opts;
    });

    const vendorNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const v of vendors ?? []) {
            map.set(v.vendorId, v.name || v.vendorId);
        }
        return map;
    }, [vendors]);

    const rows = (orders ?? []) as ProcurementOrder[];

    const columns: DataSheetColumn<ProcurementOrder>[] = useMemo(
        () => [
            {
                key: 'poNumber',
                header: 'PO Number',
                width: 160,
                sticky: true,
                render: (val: string) => (
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: textColors.primary,
                        }}
                    >
                        {val}
                    </span>
                ),
            },
            {
                key: 'vendorId',
                header: 'Vendor',
                width: 220,
                render: (val: string | null, row: ProcurementOrder) => (
                    <span
                        style={{
                            fontSize: 13,
                            color:
                                row.vendorName || val
                                    ? textColors.primary
                                    : textColors.muted,
                        }}
                    >
                        {row.vendorName ||
                            (val
                                ? vendorNameById.get(val) ?? val
                                : 'No vendor')}
                    </span>
                ),
            },
            {
                key: 'poDate',
                header: 'PO Date',
                width: 130,
                render: (val: string) => (
                    <span style={{ fontSize: 13, color: textColors.primary }}>
                        {formatDateDisplay(val)}
                    </span>
                ),
            },
            {
                key: 'status',
                header: 'Status',
                width: 160,
                render: (val: ProcurementOrderStatus) => (
                    <StatusBadge status={val} />
                ),
            },
            {
                key: 'itemCount',
                header: 'Items',
                width: 90,
                align: 'right' as const,
                render: (val: number | undefined) => (
                    <span
                        style={{
                            fontSize: 13,
                            color: textColors.primary,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatCount(val ?? 0)}
                    </span>
                ),
            },
            {
                key: 'totalUnits',
                header: 'Ordered',
                width: 110,
                align: 'right' as const,
                render: (val: number | undefined) => (
                    <span
                        style={{
                            fontSize: 13,
                            color: textColors.primary,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatCount(val ?? 0)}
                    </span>
                ),
            },
            {
                key: 'receivedUnits',
                header: 'Received',
                width: 110,
                align: 'right' as const,
                render: (val: number | undefined, row: ProcurementOrder) => {
                    const received = Number(val ?? 0);
                    const ordered = Number(row.totalUnits ?? 0);
                    const isShort = ordered > 0 && received < ordered;
                    const isMatched = ordered > 0 && received >= ordered;
                    return (
                        <span
                            style={{
                                fontSize: 13,
                                color: isMatched
                                    ? 'rgb(45, 187, 136)'
                                    : isShort && received > 0
                                      ? 'rgb(202, 138, 4)'
                                      : textColors.primary,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {formatCount(received)}
                        </span>
                    );
                },
            },
            {
                key: 'total',
                header: 'Total',
                width: 150,
                align: 'right' as const,
                render: (val: number, row: ProcurementOrder) => (
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: textColors.primary,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatMoney(val, row.currency)}
                    </span>
                ),
            },
        ],
        [vendorNameById],
    );

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <Group justify="space-between" align="flex-start">
                <PageHeader
                    icon="🧾"
                    title="Purchase Orders"
                    subtitle="Create and manage purchase orders issued to vendors"
                />
                <Button onClick={openCreate}>Create Purchase Order</Button>
            </Group>

            <Box>
                <DataSheet
                    data={rows}
                    columns={columns}
                    isLoading={isLoading}
                    getRowKey={(row) => row.id}
                    title="Purchase Orders"
                    subtitle={`${rows.length} orders`}
                    enableSearch
                    searchPlaceholder="Search by PO number..."
                    onRowClick={(row) => setDetailId(row.id)}
                    emptyState={
                        <Text size="13px" c="dimmed" ta="center" p={32}>
                            No purchase orders yet. Click "Create Purchase
                            Order" to author your first one.
                        </Text>
                    }
                />
            </Box>

            <PoFormModal
                opened={formOpen && (!workOrderPrefill || !!skuCatalog)}
                existingOrder={editingOrder}
                workOrderPrefill={workOrderPrefill}
                onClose={closeForm}
                vendors={vendors ?? []}
                skuOptions={skuCatalog ?? NO_SKU_OPTIONS}
                onSaved={() => {
                    queryClient.invalidateQueries(['procurement-orders']);
                    if (editingOrder) {
                        queryClient.invalidateQueries([
                            'procurement-order',
                            editingOrder.id,
                        ]);
                    }
                    if (workOrderPrefill) returnToWorkOrder(workOrderPrefill.workOrderId);
                }}
            />

            <ViewPoModal
                opened={detailId !== null}
                order={detailOrder ?? null}
                loading={detailLoading}
                onClose={() => setDetailId(null)}
                onSent={() => {
                    queryClient.invalidateQueries(['procurement-orders']);
                    if (detailId) {
                        queryClient.invalidateQueries([
                            'procurement-order',
                            detailId,
                        ]);
                    }
                }}
                onCloned={(newPoId) => {
                    queryClient.invalidateQueries(['procurement-orders']);
                    setDetailId(newPoId);
                }}
                onEdit={openEdit}
            />
        </div>
    );
};

export default ProcurementOrdersContainer;
